/**
 * FILE: GlobalFeed.js
 * PURPOSE: The "Main Homepage" view.
 * WHAT IT DOES: It brings together all the latest polls and news reports so you can scroll through them in one place.
 * LAYMAN SUMMARY: This is like your news feed—it's where you go to find new things to vote on.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Plus, ChevronRight } from 'lucide-react';
import NewsCard from './NewsCard';
import PollCard from './PollCard';
import { api } from '../utils/api';

export default function GlobalFeed({ polls, onVote, votedPolls, newsData, onCreate, selectedCategories, setSelectedCategories, onViewInsights, anonymousId, onSelectPoll, pollExposure, pollUpdates, registerVisibility, serverTime, onLoadMore, hasMore, isLoadingMore, savedPollsList = [], onBookmarkToggle, highlightedPollId }) {
    const scrollContainerRef = useRef(null);
    const loadMoreRef = useRef(null);

    // Infinite Scroll Logic
    useEffect(() => {
        if (!onLoadMore || !hasMore || isLoadingMore) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                onLoadMore();
            }
        }, { threshold: 0.5, rootMargin: '400px' });

        if (loadMoreRef.current) observer.observe(loadMoreRef.current);
        return () => observer.disconnect();
    }, [onLoadMore, hasMore, isLoadingMore]);

    // Filter Logic
    const filteredPolls = selectedCategories.includes('All')
        ? polls
        : polls.filter(p => selectedCategories.includes(p.category));

    // Determine active news category
    const activeNewsCategory = selectedCategories.includes('All') ? 'All' : (selectedCategories[0] || 'All');

    // Kept in sync with CreatePollModal's CATEGORIES list — those labels are exactly what
    // gets saved as poll.category, so a mismatch here means a category has no way to be
    // filtered back to (or a filter chip that can never match anything).
    const categories = ['All', 'Tech & Future', 'Finance', 'Society', 'Lifestyle', 'Politics', 'Sports', 'Science', 'Health'];

    const toggleCategory = (cat) => {
        if (cat === 'All') {
            setSelectedCategories(['All']);
        } else {
            let newCats = selectedCategories.includes('All') ? [] : [...selectedCategories];
            if (newCats.includes(cat)) {
                newCats = newCats.filter(c => c !== cat);
            } else {
                newCats.push(cat);
            }
            if (newCats.length === 0) newCats = ['All'];
            setSelectedCategories(newCats);
        }
    };

    const scrollCategories = (direction) => {
        if (scrollContainerRef.current) {
            const scrollAmount = 200;
            scrollContainerRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
        }
    };

    const placeholderNews = [
        {
            id: 'news1',
            headline: "68% Reject Centralized Governance: Is Democracy Being Redefined?",
            summary: "After 4.2 million recorded votes, the consensus establishes a clear preference for local encrypted identity over central institutional control.",
            category: "Final Verdict",
            img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=1000"
        }
    ];

    const activeNews = (newsData && newsData.length > 0) ? newsData : placeholderNews;

    return (
        <div className="w-full space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700 overflow-hidden">

            {/* Feed CTA */}
            <button
                type="button"
                onClick={onCreate}
                aria-label="Create a new poll"
                className="w-full bg-white dark:bg-gray-800 p-4 md:p-6 rounded-3xl shadow-xl shadow-purple-900/5 border border-gray-100 dark:border-gray-700 flex items-center space-x-3 md:space-x-5 cursor-pointer hover:border-primary-600/30 transition-all group"
            >
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-100 dark:bg-gray-700/50 rounded-full flex items-center justify-center text-lg md:text-xl shadow-inner group-hover:scale-110 transition-transform">
                    🤔
                </div>
                <div className="flex-1 bg-gray-50 dark:bg-gray-900 h-12 md:h-14 rounded-2xl flex items-center px-4 md:px-6 text-gray-500 font-bold text-xs md:text-sm tracking-wide group-hover:bg-white dark:group-hover:bg-gray-800 group-hover:shadow-inner transition-colors">
                    What's your opinion on...
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 bg-primary-600 rounded-xl md:rounded-2xl flex items-center justify-center text-white shadow-lg shadow-purple-500/30 group-hover:rotate-90 transition-transform" aria-hidden="true">
                    <Plus size={20} />
                </div>
            </button>

            {/* CATEGORY BAR */}
            <div className="relative group/cat">
                <button
                    onClick={() => scrollCategories('left')}
                    aria-label="Scroll categories left"
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/80 dark:bg-gray-800/80 backdrop-blur shadow-md rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300 opacity-0 group-hover/cat:opacity-100 transition-opacity hover:bg-white dark:hover:bg-gray-700"
                >
                    <ChevronRight size={16} className="rotate-180" aria-hidden="true" />
                </button>
                <div ref={scrollContainerRef} className="flex space-x-3 overflow-x-auto no-scrollbar py-1 px-1 snap-x" role="group" aria-label="Filter polls by category">
                    {categories.map(cat => {
                        const isActive = selectedCategories.includes(cat);
                        return (
                            <button
                                key={cat}
                                onClick={() => toggleCategory(cat)}
                                aria-pressed={isActive}
                                className={`snap-start flex-shrink-0 px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest transition-all border ${isActive
                                    ? 'bg-primary-600 text-white border-primary-600 shadow-lg shadow-purple-500/30'
                                    : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-100 dark:border-gray-800 hover:border-primary-600 dark:hover:border-primary-600'
                                    }`}
                            >
                                {cat}
                            </button>
                        );
                    })}
                </div>
                <button
                    onClick={() => scrollCategories('right')}
                    aria-label="Scroll categories right"
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/80 dark:bg-gray-800/80 backdrop-blur shadow-md rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300 opacity-0 group-hover/cat:opacity-100 transition-opacity hover:bg-white dark:hover:bg-gray-700"
                >
                    <ChevronRight size={16} aria-hidden="true" />
                </button>
            </div>

            {/* RENDER NEWS CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                {activeNews.map(news => (
                    <NewsCard
                        key={news.id}
                        headline={news.headline}
                        summary={news.summary}
                        category={news.category}
                        img={news.img}
                    />
                ))}
            </div>

            {/* SKELETON (Initial Load Only) */}
            {polls.length === 0 && !isLoadingMore && (
                <div className="space-y-4" aria-busy="true" aria-label="Loading polls">
                    {[1, 2].map(i => (
                        <div key={i} className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 overflow-hidden animate-pulse">
                            <div className="h-40 bg-gray-100 dark:bg-gray-700" />
                            <div className="p-5 space-y-3">
                                <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded-full w-3/4" />
                                <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full w-1/2" />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* POLL LIST */}
            {filteredPolls.map(poll => (
                <PollCard
                    key={poll.id}
                    id={poll.id}
                    voted={votedPolls[poll.id] === 'revealed'}
                    votedStatus={votedPolls[poll.id]}
                    onVote={onVote}
                    onViewInsights={() => onViewInsights(poll)}
                    question={poll.question}
                    category={poll.category}
                    isPrivate={poll.is_trap}
                    type={poll.poll_type}
                    votesToPublic={25}
                    img={poll.background_url}
                    options={poll.options}
                    postVoteInsight={poll.postVoteInsight}
                    totalVoters={poll.total_voters || 0}
                    expires_at={poll.expires_at}
                    isBounty={true}
                    bountyAmount={50}
                    compact={true}
                    onCardClick={(selection) => onSelectPoll(poll, selection)}
                    anonymousId={anonymousId}
                    creator_id={poll.creator_id}
                    pollExposure={pollExposure?.[poll.id] || 0}
                    pollUpdate={pollUpdates?.[poll.id]}
                    registerVisibility={registerVisibility}
                    serverTime={serverTime}
                    isSaved={savedPollsList.some(p => p.id === poll.id)}
                    onBookmarkToggle={onBookmarkToggle}
                    isHighlighted={poll.id === highlightedPollId}
                />
            ))}

            {/* BOTTOM LOADING ANCHOR */}
            <div ref={loadMoreRef} className="h-20 flex items-center justify-center">
                {isLoadingMore ? (
                    <div className="flex flex-col items-center animate-in fade-in duration-300">
                        <div className="w-10 h-10 border-4 border-primary-600/10 border-t-primary-600 rounded-full animate-spin mb-2"></div>
                        <span className="text-2xs font-black uppercase tracking-[0.2em] text-gray-500">Syncing Ledger...</span>
                    </div>
                ) : hasMore ? (
                    <div className="text-2xs font-black uppercase tracking-[0.2em] text-gray-300/50">Scroll for more</div>
                ) : polls.length > 0 ? (
                    <div className="text-2xs font-black uppercase tracking-[0.2em] text-gray-500">All Consensus Captured</div>
                ) : null}
            </div>
        </div>
    );
}
